// src/helpers/load-profile.ts
import type { RegistrationData } from "../contexts/auth/types";
import { profileApi } from "../api/profile/profile";
import type { Profile } from "../api/profile/profile.types";
export type ProfileMeResponse = Profile;

const normalizeAuthProvider = (
  v: string | null | undefined,
): RegistrationData["authProvider"] | undefined => {
  if (!v) return undefined;
  const p = String(v).toLowerCase();
  if (p === "email") return "email";
  if (p === "google") return "google";
  return undefined;
};

const normalizeNotificationMethod = (
  v: string | null | undefined,
): RegistrationData["notificationMethod"] | undefined => {
  if (!v) return undefined;
  const p = String(v).toLowerCase();
  if (p === "sms") return "sms";
  if (p === "email") return "email";
  if (p === "both") return "both";
  return undefined;
};

const normalizeBiometricType = (
  v: string | null | undefined,
): RegistrationData["biometricType"] | undefined => {
  if (!v) return undefined;
  const p = String(v).toLowerCase();
  if (p === "face") return "face";
  if (p === "fingerprint") return "fingerprint";
  if (p === "unknown") return "unknown";
  return undefined;
};

/**
 * Maps a Profile object to RegistrationData + the standard return shape.
 * Shared by loadProfile() (network) and loadProfileFromBootData() (pre-fetched).
 */
function mapProfileToResult(data: Profile): {
  id: string;
  data: RegistrationData;
  profile: ProfileMeResponse;
} {
  const fromProfile: Partial<RegistrationData> = {
    firstName: data.first_name ?? undefined,
    lastName: data.last_name ?? undefined,
    company: data.company_name ?? undefined,
    email: data.email_address ?? undefined,
    phoneNumber: data.mobile_number ?? undefined,
    timezone: data.preferred_timezone ?? undefined,
    nickname: data.nickname ?? undefined,

    authProvider: normalizeAuthProvider(data.auth_provider),

    registration_completed: data.registration_completed ?? undefined,
    onboarding_completed: data.onboarding_completed ?? undefined,
    registration_step: data.registration_step ?? undefined,

    biometricsEnabled: data.biometrics_enabled ?? undefined,
    biometricType: normalizeBiometricType(data.biometrics_type),
    notificationMethod: normalizeNotificationMethod(data.notification_method),
    smsConsent: data.sms_consent ?? undefined,
  };

  return {
    id: data.id,
    data: (fromProfile as RegistrationData) ?? ({} as RegistrationData),
    profile: data,
  };
}

/**
 * Loads profile from /profiles/me and returns onboarding data.
 * ✅ Prefills onboarding ONLY from the profile table.
 */
export async function loadProfile(): Promise<
  { id: string; data: RegistrationData; profile: ProfileMeResponse } | null
> {
  try {
    const data = await profileApi.get();
    return mapProfileToResult(data);
  } catch (error) {
    console.error("[loadProfile] Error loading profile:", error);
    return null;
  }
}

/**
 * Maps a pre-fetched Profile (from /boot/me) to the same shape as loadProfile().
 * No network call — uses the profile data already returned by the boot endpoint.
 */
export function loadProfileFromBootData(
  data: Profile,
): { id: string; data: RegistrationData; profile: ProfileMeResponse } | null {
  try {
    return mapProfileToResult(data);
  } catch (error) {
    console.error("[loadProfileFromBootData] Error mapping profile:", error);
    return null;
  }
}
